This program has been developed by students from the bachelor Computer Science
at Utrecht University within the Software Project course. Copyright (c) Utrecht
University (Department of Information and Computing Sciences)

<template>
  <div>
    <div class="basicBlock">
      <p class="header upper">{{ $t("lessonPackages.bundleLevels") }}</p>
      <div class="changeName" v-if="bundleSelected">
        <input
          id="nameBundle"
          class="input noUpperBorder"
          :placeholder="$t('lessonPackages.currBundleNewName')"
          v-model="activeName"
          autocomplete="off"
        />
        <div class="overlay" v-bind:class="{ overlayVisible: overlay }">
          <div class="message">
            <p class="messageHeader error">{{ this.messageTitle }}</p>
            <p>{{ this.updateMessage }}</p>
            <div class="buttonsCenter">
              <button class="button success" @click="overlay = false">
                {{ $t("general.error.confirm") }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        class="blockEntry buttonsRight upperBorder spaceAbove leftRight"
        v-if="bundleSelected"
      >
        <div class="inherit">
          <p>{{ $t("lessonPackages.allLevels") }}</p>
          <img
            class="more"
            src="@/assets/images/icons/More.svg"
            @click="toggleMoreDisplayAll()"
            alt="More buttons icon"
          />
        </div>
        <div class="oneLine">
          <div class="tooltip">
            <button
              class="button warning"
              v-bind:class="{
                selected: AllPsdClicked,
                unselected: !AllPsdClicked,
              }"
              v-on:click="toggleAllPSD"
            >
              {{ $t("lessonPackages.psd") }}
            </button>
            <div class="tooltipText top-6">
              <p>{{ $t("lessonPackages.tooltips.psd") }}</p>
              <i></i>
            </div>
          </div>
          <div class="tooltip">
            <button
              class="button warning"
              v-bind:class="{
                selected: AllCodeClicked,
                unselected: !AllCodeClicked,
              }"
              v-on:click="toggleAllCode"
            >
              {{ $t("lessonPackages.code") }}
            </button>
            <div class="tooltipText top-6">
              <p>{{ $t("lessonPackages.tooltips.code") }}</p>
              <i></i>
            </div>
          </div>
          <div class="tooltip">
            <button
              class="button warning"
              v-bind:class="{
                selected: lockedClicked,
                unselected: !lockedClicked,
              }"
              v-on:click="lockedClicked = !lockedClicked"
            >
              <img
                src="@/assets/images/icons/LockGrey.svg"
                alt="Lock bundle icon"
              />
            </button>
            <div class="tooltipText top-6">
              <p>{{ $t("lessonPackages.tooltips.lockBundle") }}</p>
              <i></i>
            </div>
          </div>
        </div>
      </div>
      <div v-if="bundleSelected">
        <draggable :list="this.levels">
          <div
            class="blockEntry selectable grid-cols-5 buttonsRight leftRight"
            v-for="(l, i) in levels"
            :key="l.bundleId"
          >
            <div class="inherit">
              <p>{{ l.name }}</p>
              <img
                class="more"
                src="@/assets/images/icons/More.svg"
                @click="toggleMoreDisplay(i)"
                alt="More buttons icon"
              />
            </div>
            <div class="oneLine">
              <div :class="this.moreDisplay[i]">
                <button
                  class="button neutral small"
                  v-on:click="toggleSelectedPSD(i)"
                  v-bind:class="{
                    selected: levels[i].allowPsd,
                    unselected: !levels[i].allowPsd,
                  }"
                >
                  {{ $t("lessonPackages.psd") }}
                </button>
                <button
                  class="button neutral small"
                  v-on:click="toggleSelectedCode(i)"
                  v-bind:class="{
                    selected: levels[i].allowWritten,
                    unselected: !levels[i].allowWritten,
                  }"
                >
                  {{ $t("lessonPackages.code") }}
                </button>
              </div>
              <button class="button warning" @click="removeLevel(l)">
                <img
                  src="@/assets/images/icons/Min.svg"
                  alt="Remove level icon"
                />
              </button>
            </div>
          </div>
        </draggable>
      </div>
      <div class="buttonsCenter" v-if="bundleSelected">
        <button
          class="button spaceAbove noUpperLine"
          :class="active.published ? 'grey' : 'success'"
          @click="commitBundle(activeName)"
        >
          {{ $t("lessonPackages.updateBundle") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { VueDraggableNext } from "vue-draggable-next";
import { bundleAPI } from "@/utils/apiCalls";
import { bundleLevelSettings, bundle } from "@/utils/Types";

@Options({
  components: {
    draggable: VueDraggableNext,
  },
  data() {
    return {
      overlay: false,
      messageTitle: null,
      updateMessage: null,
      dragging: false,
      allDisplay: false,
      AllPsdClicked: false,
      AllCodeClicked: false,
      lockedClicked: false,
      active: "",
      activeName: null,
      moreDisplay: [],
      temp: [],
      levels: [] as bundleLevelSettings[],
    };
  },
  props: {
    bundleSelected: Boolean,
  },
  methods: {
    /**
     * Save bundle
     * @param newName Name of bundle
     */
    async commitBundle(newName: string) {
      for (let level of this.levels) {
        if (!level.allowPsd && !level.allowWritten) {
          this.messageTitle = this.$t("general.error.error");
          this.updateMessage = this.$t("general.error.lessonPackage.playable");
          this.overlay = true;
          return;
        }
      }
      if (newName == "") {
        this.messageTitle = this.$t("general.error.error");
        this.updateMessage = this.$t("general.error.lessonPackage.noName");
        this.overlay = true;
      } else if (newName.length > 35) {
        this.messageTitle = this.$t("general.error.error");
        this.updateMessage = this.$t("general.error.lessonPackage.tooLong");
        this.overlay = true;
      } else {
        this.levels.map((value: bundleLevelSettings, index: number) => {
          value.index = index;
          bundleAPI.updateBundleLevelSettings(value as bundleLevelSettings);
        });
        const bundle = {
          bundleId: this.active.bundleId,
          name: newName,
          lock: this.lockedClicked,
          published: false,
        };
        await bundleAPI.updateBundle(bundle).then((err) => {
          this.$emit("updateMyBundles", this.active.bundleId, newName);
          if (err === null) {
            this.overlay = true;
            this.messageTitle = "Error";
            this.updateMessage = this.$t(
              "general.error.lessonPackage.updateInUse"
            );
          } else {
            this.overlay = true;
            this.messageTitle = this.$t("general.success.success");
            this.updateMessage = this.$t(
              "general.success.lessonPackage.updated"
            );
          }
        });
      }
    },
    /**
     * Update visible bundles
     * @param b Bundle
     */
    async updateBundleLevels(b: bundle) {
      await bundleAPI.getBundleLevelSettings(b.bundleId).then((value) => {
        this.levels = value;
        this.active = b;
      });
      this.moreDisplay = Array(this.levels.length).fill("noMoreDisplay");
      this.lockedClicked = b.lock;
    },
    /**
     * Add level to bundle
     * @param l Bundle level settings
     * @param bundle Bundle
     */
    addLevel(l: bundleLevelSettings, bundle: bundle) {
      console.log("addlevel");
      var modePSD = false;
      var modeCode = false;
      if (this.AllCodeClicked) {
        modeCode = true;
      }

      if (this.AllPsdClicked) {
        modePSD = true;
      }

      this.moreDisplay.push("noMoreDisplay");
      const obj: bundleLevelSettings = {
        bundleId: bundle.bundleId,
        levelId: l.levelId,
        name: l.name,
        index: this.levels.length + 1,
        allowPsd: modePSD,
        allowWritten: modeCode,
      };
      this.levels.push(obj);
    },
    /**
     * Remove level from bundle
     * @param l Bundle level settings
     */
    removeLevel(l: bundleLevelSettings) {
      const index = this.levels.indexOf(l, 0);
      this.$emit("removePublishedLevel", l);
      this.levels.splice(index, 1);
      this.moreDisplay.splice(index, 1);
    },
    /**
     * Toggle visibilty bundle option buttons one level
     * @param i Number of bundle in the list
     */
    toggleMoreDisplay(i: number) {
      if (this.moreDisplay[i] === "noMoreDisplay") {
        this.moreDisplay[i] = "moreDisplay";
      } else {
        this.moreDisplay[i] = "noMoreDisplay";
      }
    },
    /**
     * Toggle selected code options for one level
     * @param i Number of bundle in the list
     */
    toggleSelectedCode(i: number) {
      for (let b in this.levels) {
        if (b) {
          this.AllCodeClicked = false;
        }
      }
      this.levels[i].allowWritten = !this.levels[i].allowWritten;
    },
    //Toggle selected code options for all levels
    toggleAllCode() {
      this.AllCodeClicked = !this.AllCodeClicked;
      this.levels.map((value: bundleLevelSettings) => {
        value.allowWritten = this.AllCodeClicked;
      });
    },
    /**
     * Toggle PSD code option for one level
     * @param i Number of bundle in the list
     */
    toggleSelectedPSD(i: number) {
      for (let b in this.levels) {
        if (b) {
          this.AllPsdClicked = false;
        }
      }
      this.levels[i].allowPsd = !this.levels[i].allowPsd;
    },
    //Toggle PSD code option for all levels
    toggleAllPSD() {
      this.AllPsdClicked = !this.AllPsdClicked;
      this.levels.map((value: bundleLevelSettings) => {
        value.allowPsd = this.AllPsdClicked;
      });
    },
    //Toggle visibilty bundle option buttons all levels
    toggleMoreDisplayAll() {
      this.allDisplay = !this.allDisplay;
      if (this.allDisplay) {
        this.moreDisplay = this.moreDisplay.map(() => "moreDisplay");
      } else {
        this.moreDisplay = this.moreDisplay.map(() => "noMoreDisplay");
      }
    },
  },
})
export default class ChosenLevelsBlock extends Vue {}
</script>
